/* eslint-disable graphql/template-strings */
import { Injectable } from '@angular/core';
import { first } from 'rxjs/operators';
import gql from 'graphql-tag';

import { BuildDeviceLastAggregateQuery, BuildEnergyStateQuery } from '../queries/query-constructors.gql';
import {
  clone,
  isNotNullOrUndefined,
  isProductEnerclic,
  isProductHybrid3Phase,
  isProductMeter,
  maskEmsIfNeeded,
  minToZero,
  objectPropertiesToString,
  parseSKonAggregateDailies,
  removeAllMaskIdFromDeviceList,
} from '../utils';
import { GraphqlApiService } from '../graphql-api/graphql-api.service';
import { Inverter, InverterV2, Range } from '../telemetry';
import { Device } from '../models/firebase.model';
import { MonitoringType, ProductModelService } from '../models';

interface SupportDailyTelemetry {
  [date: string]: {
    [key: string]: Partial<Inverter>;
  };
}

@Injectable({
  providedIn: 'root',
})
export class EnergiesService {
  readonly client$ = this.graphqlApiService.client$.pipe(isNotNullOrUndefined());

  constructor(
    private readonly graphqlApiService: GraphqlApiService,
  ) {}

  async fetchVirtualEnergyTelemetry(serials: string[], tz: string, gwType: MonitoringType, refresh: boolean = false): Promise<{ [key: string ]: Partial<Inverter> }>
  {
    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';
    const devices: Device[] = serials.map((serial) => {
      const product = new ProductModelService(serial);
      const type = product.type?.name ?? 'inverter';
      if (type === 'connext' || type === 'azzurro-hub') {
        return { id: maskEmsIfNeeded(serial).toUpperCase(), type, name: serial };
      }
      return { id: serial, type, name: serial };
    }).filter((device) => ["inverter" , "meter" , "connext" , "enerclic", "azzurro-hub"].includes(device.type));

    if (devices.length === 0) {
      return {};
    }

    const query = gql`${BuildDeviceLastAggregateQuery(devices, gwType)}`;
    const { data } = clone(await client.query<{ [key: string ]: Inverter[] }>({ query, fetchPolicy }));
    // the get last query return an array of objects, we need to transform it to an object
    const energies = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, value.length ? value[0] : {}])
    );

    return removeAllMaskIdFromDeviceList(energies);
  }

  async fetchEnergiesByRange(serials: string[], gwType: MonitoringType, range: Range, refresh: boolean = false): Promise<{ [key: string ]: Partial<Inverter>[] }>
  {
    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';
    const devices: Device[] = serials.map((serial) => {
      const product = new ProductModelService(serial);
      const type = product.type?.name ?? 'inverter';
      if (type === 'connext' || type === 'azzurro-hub') {
        return { id: maskEmsIfNeeded(serial).toUpperCase(), type, name: serial };
      }
      return { id: serial, type, name: serial };
    }).filter((device) => ["inverter" , "meter" , "connext" , "enerclic", "azzurro-hub"].includes(device.type));

    if (devices.length === 0) {
      return {};
    }

    const query = gql`${BuildEnergyStateQuery(devices, range, gwType)}`;
    const { data } = clone(await client.query<{ [key: string ]: Inverter[] }>({ query, fetchPolicy }));

    return removeAllMaskIdFromDeviceList(data);
  }

  getEnergyAggregate(telemetry: { [key: string]: Array<Partial<Inverter>> }, inverterMeterSensor: boolean): Array<Partial<Inverter>> {
    const supportTelemetry: SupportDailyTelemetry = {};
    Object.keys(telemetry).forEach((devId) => {
      telemetry[devId].forEach((snapshot) => {
        const dateString = parseSKonAggregateDailies(snapshot.SK);
        if (dateString) {
          supportTelemetry[dateString] = {
            ...(supportTelemetry[dateString] || {}),
            [devId]: snapshot,
          };
        }
      });
    });

    const result = Object.keys(supportTelemetry).map(
      (dateString): Partial<Inverter> =>
        this.getSnapshotEnergyAggregate(supportTelemetry[dateString], inverterMeterSensor)
    );

    return result;
  };

  getSnapshotEnergyAggregate(telemetry: { [key: string]: Partial<Inverter> }, invertMeterSensor: boolean): Partial<Inverter> {
    const hasHybrid3Phase = Object.keys(telemetry)
      .map((devId) => new ProductModelService(devId))
      .some((pm) => isProductHybrid3Phase(pm));

    const hasMeter = Object.keys(telemetry)
      .map((devId) => new ProductModelService(devId))
      .some((pm) => isProductMeter(pm));

    const hasEnerclic = Object.keys(telemetry)
      .map((devId) => new ProductModelService(devId))
      .some((pm) => isProductEnerclic(pm));
    // @note: we want insert on the first positions all inverters
    // necessary to have a correct power when there are meters on the system
    const devices = Object.keys(telemetry).map((id) => (new ProductModelService(id)))
    const sortedKeyDevices = devices.filter((pm) => pm.type?.name === 'inverter').concat(
      devices.filter((pm) => pm.type?.name !== 'inverter')
    ).map((pm) => pm.id);

    return objectPropertiesToString(
      sortedKeyDevices.reduce(
        (prev, deviceId) => {
          const cT: Partial<Inverter & InverterV2> = { ...telemetry[deviceId] };

          const product = new ProductModelService(deviceId);
          // @note: is there is hybrid-3ph, Energy Generate is EG1 otherwise contribute is zero
          // if hybrid-3ph not present, Energy Generate is sum of all devices
          if (hasHybrid3Phase && !isProductHybrid3Phase(product)) {
            cT.EG1 = '0';
            cT.ET1 = '0';
          }

          if (hasMeter || hasEnerclic) {
            /** Attention: From the origin the imported / exported energy is exchanged (only on meter devices) */
            let temp = cT.EG3;
            cT.EG3 = cT.EG2;
            cT.EG2 = temp;

            temp = cT.ET3;
            cT.ET3 = cT.ET2;
            cT.ET2 = temp;
            if (invertMeterSensor || invertMeterSensor === undefined) {
              temp = cT.EG3;
              cT.EG3 = cT.EG2;
              cT.EG2 = temp;

              temp = cT.ET3;
              cT.ET3 = cT.ET2;
              cT.ET2 = temp;
            }

            // @note: for system with pv and meter EG4 (consumed) = EG1 - EG2 + EG3 (Generated - exported + imported)
            if (isProductMeter(product) || isProductEnerclic(product)) {
              cT.EG4 = `${ Number(cT.EG4 || '') - Number(cT.EG2) + Number(cT.EG3 || '') }`;
              cT.ET4 = `${ Number(cT.ET4 || '') - Number(cT.ET2) + Number(cT.ET3 || '') }`;
            } else {
              cT.EG4 = `${ Number(cT.EG4 || '') + Number(cT.EG1 || '') }`;
              cT.ET4 = `${ Number(cT.ET4 || '') + Number(cT.ET1 || '') }`;
            }
          }

          return {
            ...minToZero({
              EG1: prev.EG1 + Number(cT.EG1 || ''),
              EG2: prev.EG2 + Number(cT.EG2 || ''),
              EG3: prev.EG3 + Number(cT.EG3 || ''),
              EG4: prev.EG4 + Number(cT.EG4 || ''),
              EG5: prev.EG5 + Number(cT.EG5 || ''),
              EG6: prev.EG6 + Number(cT.EG6 || ''),
              SOL_EG: prev.SOL_EG + Number(cT.SOL_EG || ''),
              ET1: prev.ET1 + Number(cT.ET1 || ''),
              ET2: prev.ET2 + Number(cT.ET2 || ''),
              ET3: prev.ET3 + Number(cT.ET3 || ''),
              ET4: prev.ET4 + Number(cT.ET4 || ''),
              ET5: prev.ET5 + Number(cT.ET5 || ''),
              ET6: prev.ET6 + Number(cT.ET6 || ''),
              TS: prev.TS > Number(cT.TS) ? prev.TS : Number(cT.TS),
            }),
            SK: cT.SK,
          };
        },
        {
          EG1: 0,
          EG2: 0,
          EG3: 0,
          EG4: 0,
          EG5: 0,
          EG6: 0,
          SOL_EG: 0,
          ET1: 0,
          ET2: 0,
          ET3: 0,
          ET4: 0,
          ET5: 0,
          ET6: 0,
          TS: 0,
        }
      )
    );
  };
}

